// page-backlog-review-import.ts — Le retour du kit : déposer une réponse
// produite ailleurs.
//
// C'est la seconde moitié d'un aller-retour dont la première est la modale R4
// (« Ce qui va être transmis »). Elle vit donc dans le menu Analyser, à côté
// des deux autres passes, et NON dans R4 : au retour, l'utilisateur ne cherche
// pas ce qui sort, il rapporte ce qui rentre.
//
// Trois choix d'écran, et ils tiennent au fait que le fichier vient du dehors :
//
// 1. **Le bilan reste affiché après le chargement.** Un toast disparaîtrait
//    avec la seule liste qui dise quelles lignes ont été refusées, et pourquoi.
// 2. **Un fichier accepté dont aucune ligne n'entre s'affiche en erreur.** Le
//    serveur rend 200, la revue n'a rien gagné : le dire en vert serait faux.
// 3. **Le refus du serveur est repris tel quel.** Il porte déjà le geste à
//    faire — retélécharger un kit — et le réécrire ici le ferait diverger.

import {
  importSummaryLines,
  importTone,
  type ImportResult,
} from '../domain/backlog-review-import.js';

// Doit rester égal à `import_pass.MAX_BYTES` côté serveur. Contrôlé ici AUSSI
// pour que le refus soit immédiat : téléverser un fichier de plusieurs mégas
// pour se le voir refuser au bout est une attente pour rien.
const MAX_BYTES = 1024 * 1024;

const TONE_COLOR: Record<string, string> = {
  success: 'var(--accent)',
  warning: 'var(--warn)',
  error: 'var(--danger)',
};

function _bodyHtml(): string {
  return `<p class="text-sm">${esc(t('review.import.intro'))}</p>
    <input type="file" id="import-file" accept=".json,application/json"
      style="margin:10px 0;display:block" />
    <div class="text-muted text-xs">${esc(t('review.import.hint'))}</div>
    <div id="import-result" style="margin-top:12px"></div>`;
}

// PAS de `esc()` autour de `t()` ici : `t()` fait déjà passer chaque valeur de
// `params` par `esc()`. Un second échappement rendrait visibles les entités
// HTML — un motif « famille inconnue : l'abandon » s'afficherait
// « l&#39;abandon ». Le gabarit, lui, ne porte aucun contenu utilisateur.
// Ailleurs dans l'écran, `esc(t(clé, params))` est sans effet visible parce que
// les paramètres y sont des nombres ; ici le motif vient du fichier tiers.
function _reasonsHtml(result: ImportResult): string {
  if (!result.reasons.length) return '';
  const items = result.reasons
    .map((r) => `<li>${t('review.import.reason', { line: String(r.line), reason: r.reason })}</li>`)
    .join('');
  return `<ul class="text-xs" style="margin:8px 0 0 16px">${items}</ul>`;
}

function _resultHtml(result: ImportResult): string {
  const color = TONE_COLOR[importTone(result)] || 'var(--fg)';
  const lines = importSummaryLines(result)
    .map((l) => `<div>${esc(t(l.key, l.params))}</div>`)
    .join('');
  const warnings = result.warnings
    .map((w) => `<div class="text-muted text-xs">${esc(w)}</div>`)
    .join('');
  return `<div style="border-left:3px solid ${color};padding-left:10px">
      <div style="font-weight:600">${lines}</div>
      ${_reasonsHtml(result)}
      ${warnings}
    </div>`;
}

/** Ouvre la modale d'import. `onLoaded` n'est appelé que si quelque chose est
 *  entré : recharger la revue pour zéro recommandation ferait clignoter
 *  l'écran sans rien y changer. */
export function openImportModal(reviewId: number, onLoaded?: () => void): void {
  // La PROMESSE est rendue, pas avalée : `openModal` désactive le bouton le
  // temps de `onConfirm`, et c'est ce qui empêche un double-clic de lancer deux
  // imports du même fichier. La jeter rendrait la garde inopérante.
  openModal(
    t('review.import.title'),
    _bodyHtml(),
    () => _submit(reviewId, onLoaded),
    t('review.import.confirm'),
  );
}

async function _submit(reviewId: number, onLoaded?: () => void): Promise<void> {
  const input = document.getElementById('import-file') as HTMLInputElement | null;
  const file = input?.files?.[0];
  const zone = document.getElementById('import-result');
  if (!file) {
    toast(t('review.import.noFile'), 'error');
    return;
  }
  if (file.size > MAX_BYTES) {
    toast(t('review.import.tooLarge'), 'error');
    return;
  }
  const form = new FormData();
  form.append('file', file);
  try {
    const result = (await api.importBacklogRecommendations(reviewId, form)) as ImportResult;
    if (zone) zone.innerHTML = _resultHtml(result);
    // Le champ est vidé APRÈS un chargement réussi, pour deux raisons. La
    // visible : le même fichier ne se réimporte pas, l'empreinte du dossier a
    // changé en l'enregistrant. La sournoise : la garde « modifications non
    // enregistrées » compare la valeur du champ à celle qu'il avait à
    // l'ouverture — un champ resté rempli ferait demander confirmation en
    // fermant une modale dont le travail est pourtant terminé.
    if (input) input.value = '';
    if (result.loaded > 0) onLoaded?.();
  } catch (e: unknown) {
    // La modale reste ouverte : le refus dit quoi faire, et le fichier déposé
    // reste sélectionné pour qui veut réessayer après un nouvel export.
    toast(e instanceof Error ? e.message : t('common.error'), 'error');
  }
}
